import type { SectionId } from '@sonder/shared'
import type { MissingField } from '@sonder/shared'
import { Navigate, useOutletContext, useParams } from 'react-router'
import { useClientView } from '@/app/mode'
import { enabledSections, useBrandCount } from '@/data/client'
import { ButtonLink, Eyebrow, Icons, ProgressStrip } from '@/ui'
import { AssetsSection } from '@/sections/AssetsSection'
import { AudienceSection } from '@/sections/AudienceSection'
import { BrandsSection } from '@/sections/BrandsSection'
import { ContactsSection } from '@/sections/ContactsSection'
import { FaqSection } from '@/sections/FaqSection'
import { LinksSection } from '@/sections/LinksSection'
import { LocationsSection } from '@/sections/LocationsSection'
import { SocialSection } from '@/sections/SocialSection'
import { SpecialsSection } from '@/sections/SpecialsSection'
import type { PageProps } from './ClientRoutes'
import type { WorkspaceContext } from './WorkspaceLayout'

const COMPONENTS: Record<SectionId, React.ComponentType<{ clientId: string; config: PageProps['config'] }>> = {
  brands: BrandsSection, locations: LocationsSection, specials: SpecialsSection, contacts: ContactsSection, audience: AudienceSection,
  social: SocialSection, assets: AssetsSection, links: LinksSection, faq: FaqSection,
}

export function SectionPage({ clientId, config, base }: Omit<PageProps, 'client'>) {
  const { sectionId } = useParams()
  const clientView = useClientView()
  const { missing, progress } = useOutletContext<WorkspaceContext>()
  const sections = enabledSections(config, useBrandCount(clientId))
  const idx = sections.findIndex((s) => s.id === sectionId)
  if (idx < 0) return <Navigate to={`${base}/onboarding`} replace />
  const def = sections[idx]!
  const Comp = COMPONENTS[def.id]
  const prev = sections[idx - 1]
  const next = sections[idx + 1]
  return (
    // At least as tall as the window below the top bar, so the step navigation sits at the bottom even on short
    // steps. -mb-8 cancels the layout's bottom padding: at the end of the page the navigation rests on the
    // window's bottom edge, exactly where it is pinned while scrolling, so it never shifts.
    <div className="-mb-8 flex flex-col gap-10 lg:min-h-[calc(100dvh-var(--topbar-h,4rem)-2rem)]">
      <header className="grid max-w-form gap-3">
        <Eyebrow numeral={def.number}>{def.title}</Eyebrow>
        <h1 className="text-h2">{def.heading}</h1>
        <p className="text-charcoal">{clientView ? def.clientIntro : def.staffIntro}</p>
      </header>
      {!clientView && <StillEmpty items={missing(def.id)} />}
      <Comp key={def.id} clientId={clientId} config={config} />
      {/* Pinned to the bottom of the window while the step scrolls; settles in place at the end of the page.
          One row of buttons at every width: on a phone the back button is just its arrow, so the bar stays short.
          Below lg, where the rail's progress has scrolled out of sight, the bar carries it: a line of text above the
          buttons and the bar itself along the top edge, so each answer visibly moves it. */}
      <nav
        aria-label="Section navigation"
        className="sticky bottom-0 z-20 -mx-1 mt-auto grid gap-2.5 border-t border-gray-200 bg-paper/95 px-1 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:pt-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <ProgressStrip {...progress} className="lg:hidden" />
        <div className="flex items-center justify-between gap-3">
          {prev ? (
            <ButtonLink to={`${base}/onboarding/${prev.id}`} variant="ghost" arrow={false} icon={<Icons.ArrowLeft />} aria-label={`Back to ${prev.title}`} className="shrink-0 max-sm:w-11 max-sm:gap-0 max-sm:px-0">
              <span className="max-sm:sr-only">{prev.title}</span>
            </ButtonLink>
          ) : (
            <span />
          )}
          <div className="flex min-w-0 items-center justify-end gap-3 sm:gap-4">
            {/* Counts Review & submit as the last step, matching the rail's numbering. Left out on the narrowest
                phones, where the next step's name needs the room (the strip at the top shows where they are). */}
            <span className="shrink-0 text-small tabular-nums text-dim max-[22.5rem]:hidden">
              {idx + 1}
              <span className="max-sm:hidden"> of </span>
              <span className="sm:hidden">/</span>
              {sections.length + 1}
            </span>
            <ButtonLink to={next ? `${base}/onboarding/${next.id}` : `${base}/onboarding/review`} className="min-w-0 max-sm:px-4 max-sm:[&>svg]:hidden [&>span]:truncate">
              {next ? next.title : 'Review & submit'}
            </ButtonLink>
          </div>
        </div>
      </nav>
    </div>
  )
}

/** Scrolls to a field by its section path, puts the cursor in it and flashes it so it is easy to spot. */
function jumpTo(path: string) {
  const el = document.querySelector<HTMLElement>(`[data-field="${CSS.escape(path)}"]`)
  if (!el) return
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' })
  el.querySelector<HTMLElement>('input:not([type=hidden]), textarea, button, [tabindex="0"]')?.focus({ preventScroll: true })
  if (!reduce) el.animate([{ backgroundColor: 'var(--color-warn-wash)' }, { backgroundColor: 'transparent' }], { duration: 1800, easing: 'ease-out' })
}

/** Staff only: the required fields in this step nobody has answered yet, each a jump link. */
function StillEmpty({ items }: { items: MissingField[] }) {
  if (items.length === 0) return null
  return (
    <section aria-label="Still empty" className="grid max-w-form gap-3 border-l-2 border-warn bg-warn-wash px-5 py-4">
      <p className="text-eyebrow uppercase tracking-eyebrow text-ink">
        Still empty <span className="ml-1 text-dim tabular-nums">{items.length}</span>
      </p>
      <ul className="grid gap-1.5">
        {items.map((m) => (
          <li key={m.path}>
            <button type="button" onClick={() => jumpTo(m.path)} className="text-left text-small text-ink underline decoration-gray-300 underline-offset-4 hover:decoration-ink">
              {m.name}
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
